import type { MyEvent } from '../lib/api';

interface EventCardProps {
  event: MyEvent;
  variant?: 'today' | 'default' | 'completed' | 'unclosed';
  onClick?: () => void;
}

const MONTH_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function parseDay(iso: string): { day: string; mon: string } {
  // iso = "YYYY-MM-DD"
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return { day: '—', mon: '' };
  const day = m[3];
  const monIdx = parseInt(m[2], 10) - 1;
  return { day, mon: MONTH_ES[monIdx] || '' };
}

// Mirrors EventDetailModal.tsx:formatTime — any fix to one MUST be ported to
// the other.
function formatTime(t: string): string {
  // t arrives from miniapp-api as "H:MM AM/PM" (Sheets text rows may carry a
  // lowercase "7:00 pm" or seconds "5:00:00 PM"), or as legacy 24h "HH:MM".
  // Never re-derive the meridiem when the string already has one — doing so
  // showed every PM call time as AM (EV-0428 / EV-0441, 2026-07-18).
  const ap = t.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap])\.?m\.?$/i);
  if (ap) {
    const h = parseInt(ap[1], 10) % 12 || 12;
    return `${h}:${ap[2] || '00'} ${ap[3].toLowerCase() === 'p' ? 'PM' : 'AM'}`;
  }
  // 24h "HH:MM" → "H:MM AM/PM"
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm} ${ampm}`;
}

export function EventCard({ event, variant = 'default', onClick }: EventCardProps) {
  const { day, mon } = parseDay(event.date);
  const isToday = variant === 'today';
  const isUnclosed = variant === 'unclosed';

  return (
    <div
      class={`event ${isToday ? 'today-event' : ''} ${variant === 'completed' ? 'completed' : ''} ${isUnclosed ? 'unclosed-event' : ''}`}
      onClick={onClick}
      role="button"
    >
      <div class="date-block">
        <div class="day">{day}</div>
        <div class="mon">{mon}</div>
      </div>
      <div class="info">
        <div class="title">{event.title}</div>
        {event.startTime && (
          <div class="meta">🕖 <b>{formatTime(event.startTime)}</b>
            {event.endTime ? ` — ${formatTime(event.endTime)}` : ''}
          </div>
        )}
        {event.location && <div class="meta">📍 {event.location}</div>}
        <div class="badges">
          {isToday && <span class="live-pill">Hoy</span>}
          {isUnclosed && (
            <span class="badge sin-cerrar">
              ⚠️ Sin cerrar
              {event.daysOverdue != null && event.daysOverdue > 0
                ? ` · hace ${event.daysOverdue} ${event.daysOverdue === 1 ? 'día' : 'días'}`
                : ''}
            </span>
          )}
          {event.status === 'Confirmado' && <span class="badge confirmado">Confirmado</span>}
          {event.status === 'Asignado' && <span class="badge pendiente">Por confirmar</span>}
          {event.status === 'Completado' && <span class="badge completado">✓ Completado</span>}
          {event.isCapitan && <span class="badge capitan">Capitán</span>}
          {event.checkedIn && <span class="badge confirmado">✓ Check-in</span>}
        </div>
      </div>
    </div>
  );
}
